import {
    BreadcrumbFragment,
    CardFragment,
    ContentMatrixFragment,
    PageCardFragment,
    PageDocument,
    PageFragment,
    PageQuery,
    PageQueryVariables,
} from '../../generated/graphql';
import Seo from '../Seo';
import Hero from '../Hero';
import { heroTransformToProps } from '../../constants/imageTransforms';
import ContentWrapper from '@oberoninternal/travelbase-website/dist/components/ContentWrapper';
import { Box } from '@rebass/grid';
import Breadcrumb from '../Breadcrumb';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import ContentMatrix from '../ContentMatrix';
import React, { FC, useMemo } from 'react';
import useAnalyticsContentCategory from '@oberoninternal/travelbase-website/dist/hooks/analytics/useAnalyticsContentCategory';
import SearchWrapper from '../layout/SearchWrapper';
import PageWrapper from '@oberoninternal/travelbase-website/dist/components/Page';
import { gql } from '@apollo/client';
import initApolloClient, { PreviewData } from '../../initApolloClient';
import { GetStaticPaths, GetStaticProps } from 'next';
import { addApolloState } from '@oberoninternal/travelbase-website/dist/createApolloClient';
import addGlobalQuery from '../../utils/addGlobalQuery';
import { DEFAULT_REVALIDATION_TIME } from '../../constants/revalidationTime';
import Head from 'next/head';
import hasRedirect from '../../utils/hasRedirect';

interface Props {
    entry: PageFragment;
    breadcrumb?: BreadcrumbFragment[];
}

export const query = gql`
    query Page($uri: String, $lang: String) {
        entry(uri: [$uri], site: [$lang]) {
            ...Page
        }
    }

    fragment Page on page_Entry {
        uri
        title
        settingsCookieConsent
        settingsMetaDescription
        settingsSeoTitle
        settingsNotFindable
        heroImage {
            ...HeroTransform
        }
        heroImageVisible
        root
        contentMatrix {
            ...ContentMatrix
        }

        children {
            ...Card
        }

        id
        parent {
            id

            children(section: ["pages"]) {
                ...Card
            }
        }
        settingsSearchTags {
            title
        }

        localized {
            url
            language
        }
    }

    query PagePaths {
        entries(site: ["*"], type: ["page"]) {
            __typename
            uri
            slug
            language
        }
    }
`;

const Page: FC<React.PropsWithChildren<Props>> = ({ entry, breadcrumb }) => {
    const pageSiblings = useMemo(
        () =>
            entry.parent?.children
                ?.filter((child): child is CardFragment & PageCardFragment => child?.__typename === 'page_Entry')
                .filter(page => !page.settingsNotFindable)
                .filter(page => page.uri !== entry.uri) ?? [],
        [entry]
    );
    const pageChildren = useMemo(
        () =>
            entry.children
                ?.filter((child): child is PageCardFragment => child?.__typename === 'page_Entry')
                .filter(page => !page.settingsNotFindable) ?? [],
        [entry]
    );
    const matrix = entry.contentMatrix?.filter((field): field is ContentMatrixFragment => !!field) ?? [];

    const hasAccoBlocks = matrix.some(item => item.__typename === 'sectionAccommodations_Entry');

    useAnalyticsContentCategory(
        // if the page has an acco block, <Search /> will also call useAnalyticsContentCategory, which is enough.
        !hasAccoBlocks ? 'page' : undefined
    );
    const Wrapper: FC<React.PropsWithChildren<unknown>> = hasAccoBlocks
        ? SearchWrapper
        : ({ children }) => <>{children}</>;
    const heroImage = entry.heroImage?.[0];
    return (
        <PageWrapper>
            <Seo
                image={heroImage?.ogImageUrl ?? ''}
                settingsMetaDescription={entry.settingsMetaDescription ?? ''}
                settingsSeoTitle={entry.settingsSeoTitle ?? entry.title ?? ''}
                alternates={entry.localized}
            />
            {entry.heroImageVisible && heroImage && <Hero {...heroTransformToProps(heroImage)} />}

            {entry.settingsNotFindable ? (
                <Head>
                    <meta name="robots" content="noindex,nofollow" />
                </Head>
            ) : null}

            <Wrapper>
                <ContentWrapper>
                    <Box mt={[3, 8]} mb={[3, 5]}>
                        <Breadcrumb menu={breadcrumb} uri={entry.uri} />

                        <Title variant="large">{entry.title}</Title>
                    </Box>
                </ContentWrapper>

                <Box mb={[5, 8]}>
                    <ContentMatrix matrix={matrix} pageSiblings={pageSiblings} pageChildren={pageChildren} />
                </Box>
            </Wrapper>
        </PageWrapper>
    );
};

export const getStaticProps: GetStaticProps = async context => {
    if (context.locale === 'default') return { props: {} };

    const { previewData, params } = context;
    const lang = context.locale as string;
    const client = initApolloClient(lang, { previewData: previewData as PreviewData });
    const uri = (context.params?.uri as string[]).join('/');

    const { data } = await client.query<PageQuery, PageQueryVariables>({
        query: PageDocument,
        variables: {
            uri,
            lang,
        },
    });

    if (!data.entry) {
        const redirect = await hasRedirect((params?.uri as string[]).join('/'), lang);

        if (redirect) {
            const { redirectDestUrl, redirectHttpCode } = redirect;
            return {
                redirect: {
                    permanent: redirectHttpCode === 301,
                    destination: redirectDestUrl,
                },
                revalidate: DEFAULT_REVALIDATION_TIME,
            };
        }

        return {
            notFound: true,
        };
    }

    const suppressCookieConsent = !(
        (data?.entry as { settingsCookieConsent?: boolean })?.settingsCookieConsent ?? false
    );

    return {
        ...addApolloState(
            client,
            (await addGlobalQuery(
                {
                    props: {
                        entry: data.entry,
                        suppressCookieConsent,
                    },
                },
                lang
            )) as { props: Record<string, unknown> }
        ),
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export const getStaticPaths: GetStaticPaths = async () => {
    // const client = initApolloClient(defaultLanguage);

    // const { data } = await client.query<PagePathsQuery>({
    //     query: PagePathsDocument,
    // });

    const paths: [] =
        // data.entries
        //     ?.filter(entry => entry && popularPages.includes(`/${entry.language ?? defaultLanguage}/${entry.uri}/`))
        //     .map(entry => {
        //         const uri = entry?.uri?.split('/') ?? [];
        //         return {
        //             params: {
        //                 locale: entry?.language ?? defaultLanguage,
        //                 uri,
        //             },
        //         };
        //     }) ??
        [];

    // eslint-disable-next-line no-console
    // console.log(`Page.getStaticPaths: including ${paths.length} out of ${data.entries?.length ?? 0} paths`);

    return {
        paths,
        fallback: 'blocking',
    };
};

export default Page;
